import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { toast } from 'sonner';
import { playNotificationSound } from '@/utils/notificationSounds';

export interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'critical';
  read: boolean;
  read_at: string | null;
  metadata: any;
  created_at: string;
  user_id: string | null;
  target_roles: string[] | null;
}

export const useNotifications = () => {
  const { userId, companyId, userRole } = useStableAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Determinar se notificação broadcast foi lida via notification_reads
  const checkBroadcastReadStatus = useCallback(async (notificationIds: string[]): Promise<Set<string>> => {
    if (!userId || notificationIds.length === 0) return new Set();
    
    const { data } = await supabase
      .from('notification_reads')
      .select('notification_id')
      .eq('user_id', userId)
      .in('notification_id', notificationIds);
    
    return new Set((data || []).map(r => r.notification_id));
  }, [userId]);

  const fetchNotifications = useCallback(async () => {
    if (!companyId || !userId) return;

    try {
      // RLS agora filtra corretamente por user_id, target_roles e company_id
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('company_id', companyId)
        .or(`user_id.eq.${userId},user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const allNotifications = (data || []) as Notification[];
      
      // Identificar broadcasts para verificar read status via notification_reads
      const broadcastIds = allNotifications
        .filter(n => n.user_id === null)
        .map(n => n.id);
      
      const readBroadcastIds = await checkBroadcastReadStatus(broadcastIds);
      
      // Aplicar read status: broadcasts usam notification_reads, diretas usam campo read
      const enrichedNotifications = allNotifications.map(n => {
        if (n.user_id === null) {
          const isRead = readBroadcastIds.has(n.id);
          return { ...n, read: isRead, read_at: isRead ? n.read_at : null };
        }
        return n;
      });

      setNotifications(enrichedNotifications);
      setUnreadCount(enrichedNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, checkBroadcastReadStatus]);

  const markAsRead = async (notificationId: string) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification || !userId) return;

      if (notification.user_id === null) {
        // Broadcast: inserir em notification_reads
        const { error } = await supabase
          .from('notification_reads')
          .upsert({ notification_id: notificationId, user_id: userId }, { onConflict: 'notification_id,user_id' });
        if (error) throw error;
      } else {
        // Direta: marcar read na tabela notifications
        const { error } = await supabase
          .from('notifications')
          .update({ read: true, read_at: new Date().toISOString() })
          .eq('id', notificationId);
        if (error) throw error;
      }

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const markAllAsRead = async () => {
    if (!companyId || !userId) return;

    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      
      // Separar diretas e broadcasts
      const directUnread = unreadNotifications.filter(n => n.user_id !== null);
      const broadcastUnread = unreadNotifications.filter(n => n.user_id === null);

      // Marcar diretas como lidas na tabela notifications
      if (directUnread.length > 0) {
        const { error } = await supabase
          .from('notifications')
          .update({ read: true, read_at: new Date().toISOString() })
          .eq('company_id', companyId)
          .eq('user_id', userId)
          .eq('read', false);
        if (error) throw error;
      }

      // Marcar broadcasts como lidas via notification_reads
      if (broadcastUnread.length > 0) {
        const reads = broadcastUnread.map(n => ({
          notification_id: n.id,
          user_id: userId,
        }));
        const { error } = await supabase
          .from('notification_reads')
          .upsert(reads, { onConflict: 'notification_id,user_id' });
        if (error) throw error;
      }

      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
      toast.error('Erro ao atualizar notificações');
    }
  };

  // Real-time subscription
  useEffect(() => {
    if (!companyId || !userId) return;

    fetchNotifications();

    const handleNewNotification = (payload: any) => {
      const newNotification = payload.new as Notification;
      
      // RLS já filtra no banco, mas mantemos check de segurança
      if (newNotification.user_id !== null && newNotification.user_id !== userId) {
        return;
      }
      
      setNotifications(prev => [{ ...newNotification, read: false }, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      playNotificationSound(newNotification.notification_type);
      
      toast(newNotification.title, {
        description: newNotification.message,
        duration: 5000
      });
    };

    const handleNotificationUpdate = (payload: any) => {
      const updated = payload.new as Notification;
      const old = payload.old as Notification;
      
      if (!old.read && updated.read) {
        setNotifications(prev =>
          prev.map(n => n.id === updated.id ? { ...n, read: true, read_at: updated.read_at } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    };

    const userSubscription = supabase
      .channel('notifications-user')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        handleNewNotification
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        handleNotificationUpdate
      )
      .subscribe();

    const broadcastSubscription = supabase
      .channel('notifications-broadcast')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `company_id=eq.${companyId}` },
        (payload) => {
          if (payload.new.user_id === null) {
            handleNewNotification(payload);
          }
        }
      )
      .subscribe();

    return () => {
      userSubscription.unsubscribe();
      broadcastSubscription.unsubscribe();
    };
  }, [companyId, userId, userRole, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
};
